import { Check, Copy, MessageCircle, TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { buildReferralUrl, buildWhatsAppShareUrl, copyText } from '../lib/format';

type CopyState = 'idle' | 'copied' | 'failed';

function registrationUrlFor(memberId: string): string {
  const configuredOrigin = import.meta.env.VITE_PUBLIC_APP_URL?.trim();
  try {
    return buildReferralUrl(memberId, configuredOrigin || window.location.origin);
  } catch {
    return buildReferralUrl(memberId, window.location.origin);
  }
}

export function ReferralCard({ memberId }: { memberId: string }) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const resetTimer = useRef<number | undefined>(undefined);
  const registrationUrl = registrationUrlFor(memberId);
  const whatsappShareUrl = buildWhatsAppShareUrl(memberId, registrationUrl);

  useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  const copyRegistrationLink = async () => {
    const copied = await copyText(registrationUrl);
    setCopyState(copied ? 'copied' : 'failed');
    window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setCopyState('idle'), 2_000);
  };

  return (
    <section className="panel referral-card" aria-labelledby={`referral-heading-${memberId}`}>
      <div>
        <span className="eyebrow">YOUR REGISTRATION LINK</span>
        <h2 id={`referral-heading-${memberId}`}>Invite with {memberId}</h2>
        <p>Your active B-Dynamic ID is the referral ID. It is prefilled for the new member and verified again on-chain before signing.</p>
      </div>
      <div className="referral-actions">
        <div className="referral-link">
          <span title={registrationUrl}>{registrationUrl}</span>
          <button type="button" aria-label="Copy referral link" onClick={() => void copyRegistrationLink()}>
            {copyState === 'copied' ? <Check size={16} /> : copyState === 'failed' ? <TriangleAlert size={16} /> : <Copy size={16} />}
            <span aria-live="polite">{copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Retry copy' : 'Copy link'}</span>
          </button>
        </div>
        <a
          className="button whatsapp-share"
          href={whatsappShareUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`Share ${memberId} registration link on WhatsApp`}
        >
          <MessageCircle size={16} /> Share with WhatsApp
        </a>
      </div>
    </section>
  );
}
